import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { CheckCircle, XCircle, AlertCircle, Plane, Building2, Users, Clock, History } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PendingTasks = () => {
  const [activeTab, setActiveTab] = useState('flights');
  const [pendingFlights, setPendingFlights] = useState([]);
  const [pendingAgencies, setPendingAgencies] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [rejectType, setRejectType] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const { toast } = useToast();

  const formatDate = (dateString) => {
    if (!dateString) return '';
    if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) return dateString;
    
    let date;
    if (dateString.includes('T')) {
      date = new Date(dateString);
    } else if (dateString.includes('-')) {
      const [year, month, day] = dateString.split('-');
      date = new Date(year, parseInt(month) - 1, day);
    } else {
      date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleString('en-GB', { 
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.substring(0, 5);
  };

  const fetchAllData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      // Fetch pending flights
      const flightsRes = await axios.get(`${BACKEND_URL}/api/flights/pending`, { headers });
      setPendingFlights(flightsRes.data);

      // Fetch agencies (filter pending ones)
      const agenciesRes = await axios.get(`${BACKEND_URL}/api/agencies`, { headers });
      setPendingAgencies(agenciesRes.data.filter(a => a.status === 'pending_approval'));

      // Fetch pending users
      const usersRes = await axios.get(`${BACKEND_URL}/api/users/pending`, { headers });
      setPendingUsers(usersRes.data);

      // Fetch approval history
      await fetchApprovalHistory();
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({ variant: "destructive", title: "Error", description: "Failed to fetch pending tasks" });
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovalHistory = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/approval-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApprovalHistory(response.data);
    } catch (error) {
      console.error('Error fetching approval history:', error);
      // If endpoint doesn't exist yet, set empty array
      setApprovalHistory([]);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // Flight handlers
  const handleApproveFlight = async (flightId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/flights/${flightId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: "Flight approved successfully" });
      setPendingFlights(pendingFlights.filter(f => f.id !== flightId));
      fetchApprovalHistory();
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: error.response?.data?.detail || "Failed to approve flight" });
    }
  };

  // Agency handlers
  const handleApproveAgency = async (agencyId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/agencies/${agencyId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: "Agency approved successfully" });
      setPendingAgencies(pendingAgencies.filter(a => a.id !== agencyId));
      fetchApprovalHistory();
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: error.response?.data?.detail || "Failed to approve agency" });
    }
  };

  // User handlers
  const handleApproveUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/users/${userId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: "Success", description: "User approved successfully" });
      setPendingUsers(pendingUsers.filter(u => u.id !== userId));
      fetchApprovalHistory();
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: error.response?.data?.detail || "Failed to approve user" });
    }
  };

  // Generic reject handler
  const handleReject = (item, type) => {
    setSelectedItem(item);
    setRejectType(type);
    setRejectionReason('');
    setShowRejectDialog(true);
  };

  const confirmReject = async () => {
    if (!rejectionReason.trim()) {
      toast({ variant: "destructive", title: "Error", description: "Please provide a reason for rejection" });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      
      switch (rejectType) {
        case 'flight':
          endpoint = `${BACKEND_URL}/api/flights/${selectedItem.id}/reject`;
          break;
        case 'agency':
          endpoint = `${BACKEND_URL}/api/agencies/${selectedItem.id}/reject?reason=${encodeURIComponent(rejectionReason)}`;
          break;
        case 'user':
          endpoint = `${BACKEND_URL}/api/users/${selectedItem.id}/reject`;
          break;
        default:
          return;
      }

      await axios.post(endpoint, { reason: rejectionReason }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({ title: "Success", description: `${rejectType.charAt(0).toUpperCase() + rejectType.slice(1)} rejected successfully` });

      // Update the appropriate list
      switch (rejectType) {
        case 'flight':
          setPendingFlights(pendingFlights.filter(f => f.id !== selectedItem.id));
          break;
        case 'agency':
          setPendingAgencies(pendingAgencies.filter(a => a.id !== selectedItem.id));
          break;
        case 'user':
          setPendingUsers(pendingUsers.filter(u => u.id !== selectedItem.id));
          break;
      }

      setShowRejectDialog(false);
      setSelectedItem(null);
      setRejectionReason('');
      fetchApprovalHistory();
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: error.response?.data?.detail || "Failed to reject" });
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center" style={{ color: '#E8E8E8' }}>Loading pending tasks...</div>
      </div>
    );
  }

  const totalPending = pendingFlights.length + pendingAgencies.length + pendingUsers.length;

  return (
    <div className="container mx-auto p-6">
      <Card style={{ background: 'rgba(30, 42, 74, 0.6)', borderColor: '#4A5A8A' }}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ color: '#E8E8E8' }}>
            <AlertCircle className="h-6 w-6 text-orange-500" />
            Pending Tasks ({totalPending})
          </CardTitle>
          <CardDescription style={{ color: '#B0B0B0' }}>
            Review and approve or reject pending requests
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div 
              className="p-4 rounded-lg cursor-pointer hover:scale-105 transition-transform"
              style={{ background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.4)' }}
              onClick={() => setActiveTab('flights')}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded" style={{ background: 'rgba(245, 158, 11, 0.3)' }}>
                  <Plane className="h-5 w-5" style={{ color: '#F59E0B' }} />
                </div>
                <div>
                  <p className="text-2xl font-bold" style={{ color: '#F59E0B' }}>{pendingFlights.length}</p>
                  <p className="text-sm" style={{ color: '#B0B0B0' }}>Pending Flights</p>
                </div>
              </div>
            </div>

            <div 
              className="p-4 rounded-lg cursor-pointer hover:scale-105 transition-transform"
              style={{ background: 'rgba(139, 92, 246, 0.15)', border: '1px solid rgba(139, 92, 246, 0.4)' }}
              onClick={() => setActiveTab('agencies')}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded" style={{ background: 'rgba(139, 92, 246, 0.3)' }}>
                  <Building2 className="h-5 w-5" style={{ color: '#8B5CF6' }} />
                </div>
                <div>
                  <p className="text-2xl font-bold" style={{ color: '#8B5CF6' }}>{pendingAgencies.length}</p>
                  <p className="text-sm" style={{ color: '#B0B0B0' }}>Pending Agencies</p>
                </div>
              </div>
            </div>

            <div 
              className="p-4 rounded-lg cursor-pointer hover:scale-105 transition-transform"
              style={{ background: 'rgba(236, 72, 153, 0.15)', border: '1px solid rgba(236, 72, 153, 0.4)' }}
              onClick={() => setActiveTab('users')}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded" style={{ background: 'rgba(236, 72, 153, 0.3)' }}>
                  <Users className="h-5 w-5" style={{ color: '#EC4899' }} />
                </div>
                <div>
                  <p className="text-2xl font-bold" style={{ color: '#EC4899' }}>{pendingUsers.length}</p>
                  <p className="text-sm" style={{ color: '#B0B0B0' }}>Pending Users</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4" style={{ background: 'rgba(10, 17, 40, 0.5)', borderColor: '#4A5A8A' }}>
              <TabsTrigger value="flights" style={{ color: activeTab === 'flights' ? '#F59E0B' : '#B0B0B0' }}>
                <Plane className="w-4 h-4 mr-2" /> Flights ({pendingFlights.length})
              </TabsTrigger>
              <TabsTrigger value="agencies" style={{ color: activeTab === 'agencies' ? '#8B5CF6' : '#B0B0B0' }}>
                <Building2 className="w-4 h-4 mr-2" /> Agencies ({pendingAgencies.length})
              </TabsTrigger>
              <TabsTrigger value="users" style={{ color: activeTab === 'users' ? '#EC4899' : '#B0B0B0' }}>
                <Users className="w-4 h-4 mr-2" /> Users ({pendingUsers.length})
              </TabsTrigger>
              <TabsTrigger value="history" style={{ color: activeTab === 'history' ? '#22C55E' : '#B0B0B0' }}>
                <History className="w-4 h-4 mr-2" /> History
              </TabsTrigger>
            </TabsList>

            {/* Pending Flights Tab */}
            <TabsContent value="flights">
              {pendingFlights.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
                  <p style={{ color: '#B0B0B0' }}>No pending flights to review</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingFlights.map((flight) => (
                    <Card key={flight.id} className="border-l-4" style={{ borderLeftColor: '#F59E0B', background: 'rgba(10, 17, 40, 0.5)', borderColor: '#4A5A8A' }}>
                      <CardContent className="pt-6">
                        <div className="mb-4 pb-4 border-b" style={{ borderColor: '#4A5A8A' }}>
                          <div className="flex items-center gap-2">
                            <p className="text-sm" style={{ color: '#B0B0B0' }}>Requested by:</p>
                            <Badge style={{ background: 'rgba(59, 130, 246, 0.2)', color: '#3B82F6' }}>
                              {flight.agency_name || 'Unknown Agency'}
                            </Badge>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <p className="text-sm" style={{ color: '#B0B0B0' }}>Flight Number</p>
                            <p className="font-semibold text-lg" style={{ color: '#E8E8E8' }}>{flight.flight_number}</p>
                          </div>
                          <div>
                            <p className="text-sm" style={{ color: '#B0B0B0' }}>Date & Time</p>
                            <p className="font-semibold" style={{ color: '#E8E8E8' }}>
                              {formatDate(flight.flight_date)} {formatTime(flight.flight_time)}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm" style={{ color: '#B0B0B0' }}>Route</p>
                            <p className="font-semibold" style={{ color: '#E8E8E8' }}>
                              {flight.departure_airport || 'N/A'} → {flight.arrival_airport || 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm" style={{ color: '#B0B0B0' }}>Passengers</p>
                            <p className="font-semibold" style={{ color: '#E8E8E8' }}>{flight.passenger_count}</p>
                          </div>
                        </div>

                        <div className="flex gap-2 mt-6">
                          <Button onClick={() => handleApproveFlight(flight.id)} className="bg-green-600 hover:bg-green-700 text-white">
                            <CheckCircle className="mr-2 h-4 w-4" /> Approve
                          </Button>
                          <Button onClick={() => handleReject(flight, 'flight')} variant="destructive">
                            <XCircle className="mr-2 h-4 w-4" /> Reject
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Pending Agencies Tab */}
            <TabsContent value="agencies">
              {pendingAgencies.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
                  <p style={{ color: '#B0B0B0' }}>No pending agency requests</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingAgencies.map((agency) => (
                    <Card key={agency.id} className="border-l-4" style={{ borderLeftColor: '#8B5CF6', background: 'rgba(10, 17, 40, 0.5)', borderColor: '#4A5A8A' }}>
                      <CardContent className="pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <p className="text-sm" style={{ color: '#B0B0B0' }}>Agency Name</p>
                            <p className="font-semibold text-lg" style={{ color: '#E8E8E8' }}>{agency.name}</p>
                          </div>
                          <div>
                            <p className="text-sm" style={{ color: '#B0B0B0' }}>Contact Person</p>
                            <p className="font-semibold" style={{ color: '#E8E8E8' }}>{agency.contact_person || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-sm" style={{ color: '#B0B0B0' }}>Email</p>
                            <p className="font-semibold" style={{ color: '#E8E8E8' }}>{agency.email}</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                          <div>
                            <p className="text-sm" style={{ color: '#B0B0B0' }}>Phone</p>
                            <p className="font-semibold" style={{ color: '#E8E8E8' }}>{agency.phone || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-sm" style={{ color: '#B0B0B0' }}>Address</p>
                            <p className="font-semibold" style={{ color: '#E8E8E8' }}>{agency.address || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-sm" style={{ color: '#B0B0B0' }}>Requested At</p>
                            <p className="font-semibold" style={{ color: '#E8E8E8' }}>{formatDateTime(agency.created_at)}</p>
                          </div>
                        </div>

                        <div className="flex gap-2 mt-6">
                          <Button onClick={() => handleApproveAgency(agency.id)} className="bg-green-600 hover:bg-green-700 text-white">
                            <CheckCircle className="mr-2 h-4 w-4" /> Approve
                          </Button>
                          <Button onClick={() => handleReject(agency, 'agency')} variant="destructive">
                            <XCircle className="mr-2 h-4 w-4" /> Reject
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Pending Users Tab */}
            <TabsContent value="users">
              {pendingUsers.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
                  <p style={{ color: '#B0B0B0' }}>No pending user requests</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingUsers.map((user) => (
                    <Card key={user.id} className="border-l-4" style={{ borderLeftColor: '#EC4899', background: 'rgba(10, 17, 40, 0.5)', borderColor: '#4A5A8A' }}>
                      <CardContent className="pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <p className="text-sm" style={{ color: '#B0B0B0' }}>Full Name</p>
                            <p className="font-semibold text-lg" style={{ color: '#E8E8E8' }}>{user.full_name}</p>
                          </div>
                          <div>
                            <p className="text-sm" style={{ color: '#B0B0B0' }}>Email</p>
                            <p className="font-semibold" style={{ color: '#E8E8E8' }}>{user.email}</p>
                          </div>
                          <div>
                            <p className="text-sm" style={{ color: '#B0B0B0' }}>Role</p>
                            <Badge style={{ background: 'rgba(139, 92, 246, 0.2)', color: '#8B5CF6' }}>
                              {user.role}
                            </Badge>
                          </div>
                          <div>
                            <p className="text-sm" style={{ color: '#B0B0B0' }}>Phone</p>
                            <p className="font-semibold" style={{ color: '#E8E8E8' }}>{user.phone || 'N/A'}</p>
                          </div>
                        </div>

                        <div className="flex gap-2 mt-6">
                          <Button onClick={() => handleApproveUser(user.id)} className="bg-green-600 hover:bg-green-700 text-white">
                            <CheckCircle className="mr-2 h-4 w-4" /> Approve
                          </Button>
                          <Button onClick={() => handleReject(user, 'user')} variant="destructive">
                            <XCircle className="mr-2 h-4 w-4" /> Reject
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* History Tab */}
            <TabsContent value="history">
              {approvalHistory.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="h-12 w-12 mx-auto mb-2" style={{ color: '#B0B0B0' }} />
                  <p style={{ color: '#B0B0B0' }}>No approval history yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {approvalHistory.map((item, index) => (
                    <div key={index} className="flex items-center gap-4 p-4 rounded" style={{ background: 'rgba(10, 17, 40, 0.5)', border: '1px solid #4A5A8A' }}>
                      <div className={`p-2 rounded ${item.action === 'approved' ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                        {item.action === 'approved' ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge style={{ 
                            background: item.type === 'flight' ? 'rgba(245, 158, 11, 0.2)' : 
                                       item.type === 'agency' ? 'rgba(139, 92, 246, 0.2)' : 'rgba(236, 72, 153, 0.2)',
                            color: item.type === 'flight' ? '#F59E0B' : 
                                   item.type === 'agency' ? '#8B5CF6' : '#EC4899'
                          }}>
                            {item.type}
                          </Badge>
                          <span style={{ color: '#E8E8E8', fontWeight: '600' }}>{item.name}</span>
                        </div>
                        <p className="text-sm" style={{ color: '#B0B0B0' }}>
                          {item.action === 'approved' ? 'Approved' : 'Rejected'} by {item.approved_by} • {formatDateTime(item.timestamp)}
                        </p>
                        {item.reason && (
                          <p className="text-sm mt-1" style={{ color: '#EF4444' }}>Reason: {item.reason}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Rejection Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#E8E8E8' }}>
              Reject {rejectType.charAt(0).toUpperCase() + rejectType.slice(1)}
            </DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              Please provide a reason for rejection
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label style={{ color: '#E8E8E8' }}>Rejection Reason *</Label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter the reason for rejection"
                rows={4}
                style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowRejectDialog(false); setRejectionReason(''); }}
              style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}
            >
              Cancel
            </Button>
            <Button onClick={confirmReject} variant="destructive">
              Confirm Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PendingTasks;
